import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpClientModule } from '@angular/common/http';
import { BudgetService, BudgetAssignment, BudgetLine } from '../../../shared/services/budget.service';
import { environment } from '../../../../environments/environment';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SingleSelectDropdownComponent } from '../business-details/single-select-dropdown.component';

@Component({
  selector: 'app-budget-selector',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, HttpClientModule, SingleSelectDropdownComponent],
  templateUrl: './budget-selector.component.html',
  styleUrls: ['./budget-selector.component.css']
})
export class BudgetSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() totalCost: number | null = null;
  private lastTotalCost: number | null = null;
  private _availableBudgets: any[] = [];
  @Input() set availableBudgets(value: any[]) {
    // Normalize budget objects to always use budgetID
    this._availableBudgets = (value || []).map(budget => ({
      ...budget,
      budgetID: budget.budgetID || budget.budgetId, // Normalize to always use budgetID
      budgetId: budget.budgetID || budget.budgetId  // Keep both for backward compatibility
    }));
    // Do not auto-add or auto-fill assignments on budget changes

    // Emit current assignments to update parent
    this.emitAssignments();

    // Preload period labels for each budget to display in the dropdown
    this.preloadBudgetPeriodLabels();
  }

  get budgetOptions(): { value: string; label: string }[] {
    return this._availableBudgets.map(b => ({
      value: String(b.budgetID),
      label: `${b.budgetName} (${this.getBudgetPeriodLabel(b)})`
    }));
  }

  onMobileBudgetChange(index: number, value: string | null): void {
    const ctrl = this.assignments.at(index);
    if (!ctrl) return;
    ctrl.get('budgetID')?.setValue(value ? +value : null);
    this.hasUserInteracted = true;
    this.onAmountChange();
  }

  // Safe accessor for template to read the computed label
  getBudgetPeriodLabel(budget: any): string {
    const key = String(budget?.budgetID ?? budget?.budgetId ?? '');
    return this.budgetPeriodLabel[key] || 'Period';
  }
  get availableBudgets(): any[] {
    return this._availableBudgets;
  }
  private _productId: number | null = null;
  private _productName: string = '';

  @Input() set productId(value: number | null) {
    this._productId = value;
    // Clear assignments when product changes
    this.assignments.clear();
    if (value) {
      this.loadProductCost();
      // Add a single empty row prompting user to select a budget and amount
      this.addEmptyAssignmentRow();
    } else {
      this.emitAssignments();
    }
  }

  get productId(): number | null {
    return this._productId;
  }

  @Input() set productName(value: string) {
    this._productName = value || '';
  }

  get productName(): string {
    return this._productName;
  }
  @Input() isMobile = false;

  @Output() assignmentsChange = new EventEmitter<BudgetAssignment[]>();
  @Output() assignmentsSave = new EventEmitter<BudgetAssignment[]>();

  // Track user interaction for validation messages
  hasUserInteracted = false;

  form: FormGroup;
  isLoading = false;
  error: string | null = null;

  // Alert system properties
  showAlert = false;
  alertTitle = '';
  alertMessage = '';
  alertType: 'success' | 'error' = 'success';

  // Cached period labels per budget ID for dropdown display
  budgetPeriodLabel: Record<string, string> = {};

  // Map of fiscalPeriodID -> fiscal period object (with startDate, endDate, periodType)
  private fiscalPeriodsMap: Map<number, any> = new Map();

  // Load fiscal periods once (to resolve month/quarter labels)
  private loadFiscalPeriods(headers: HttpHeaders) {
    if (this.fiscalPeriodsMap.size > 0) {
      return of(Array.from(this.fiscalPeriodsMap.values()));
    }

    return this.http.get<any[]>(`${environment.apiUrl}/api/FiscalPeriods/GetAll`, { headers }).pipe(
      catchError(err => {
        console.error('Failed to load fiscal periods for budget labels:', err);
        return of([]);
      })
    );
  }

  // Compute a human-readable period label from budget lines
  private computeLabelFromLines(budget: any, lines: BudgetLine[]): string {
    try {
      if (!Array.isArray(lines) || lines.length === 0) {
        // Graceful fallback
        return 'Period';
      }

      const periods = lines
        .map(l => this.fiscalPeriodsMap.get(l.fiscalPeriodID))
        .filter((p: any) => !!p);

      if (periods.length === 0) {
        return 'Period';
      }

      const toDate = (p: any) => new Date(p?.startDate ?? p?.StartDate ?? new Date());
      const toEnd = (p: any) => new Date(p?.endDate ?? p?.EndDate ?? (p?.startDate ?? p?.StartDate));

      const starts = periods.map(toDate);
      const ends = periods.map(toEnd);
      const minStart = new Date(Math.min(...starts.map(d => d.getTime())));
      const maxEnd = new Date(Math.max(...ends.map(d => d.getTime())));

      const typeFromBudget = (budget?.budgetType ?? budget?.BudgetType ?? '').toString().toLowerCase();
      const typeFromPeriods = (periods[0]?.periodType ?? periods[0]?.PeriodType ?? '').toString().toLowerCase();
      const type = (typeFromBudget || typeFromPeriods);

      const fmtM = (d: Date) => d.toLocaleDateString('en-ZA', { month: 'short' });

      if (type.includes('quarter')) {
        const m = minStart.getMonth();
        const q = Math.floor(m / 3) + 1;
        return `Quarterly Q${q}`; // Intentionally omit raw fiscal year value
      }

      if (type.includes('annual') || type === 'yearly' || type === 'year') {
        return 'Annually';
      }

      // Monthly or unknown: if within same month/year show single month; otherwise show condensed range
      const sameMY = minStart.getFullYear() === maxEnd.getFullYear() && minStart.getMonth() === maxEnd.getMonth();
      const year = minStart.getFullYear();
      const label = sameMY ? fmtM(minStart) : `${fmtM(minStart)} - ${fmtM(maxEnd)}`;
      return `Monthly ${label}  ${year}`;
    } catch (e) {
      console.error('Error computing period label:', e);
      return 'Period';
    }
  }

  // Fetch budget lines for each available budget and compute display labels
  private preloadBudgetPeriodLabels(): void {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !Array.isArray(this._availableBudgets) || this._availableBudgets.length === 0) {
        this.budgetPeriodLabel = {};
        return;
      }

      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      });

      this.loadFiscalPeriods(headers).subscribe((periods: any[]) => {
        // Cache periods into the map
        (periods || []).forEach((p: any) => {
          const id = p?.fiscalPeriodID ?? p?.FiscalPeriodID;
          if (id != null) this.fiscalPeriodsMap.set(Number(id), p);
        });

        const requests = this._availableBudgets.map(b =>
          this.budgetService.getBudgetLinesByBudgetId(Number(b.budgetID), headers).pipe(
            catchError(err => {
              console.error('Failed to load budget lines for budget', b?.budgetID, err);
              return of([] as BudgetLine[]);
            })
          )
        );

        if (requests.length === 0) return;

        forkJoin(requests).subscribe((allLines: BudgetLine[][]) => {
          const labels: Record<string, string> = {};
          this._availableBudgets.forEach((b, idx) => {
            const lines = allLines[idx] || [];
            const label = this.computeLabelFromLines(b, lines);
            labels[String(b.budgetID)] = label;
          });
          this.budgetPeriodLabel = labels;
        });
      });
    } catch (err) {
      console.error('Error preloading budget period labels:', err);
    }
  }

  // Navigate to the costing page
  navigateToCosting(event: Event): void {
    event.preventDefault();
    this.router.navigate(['/costing']);
  }

  constructor(
    private fb: FormBuilder,
    private budgetService: BudgetService,
    private http: HttpClient,
    private router: Router
  ) {
    this.form = this.fb.group({
      assignments: this.fb.array([])
    });
  }

  private showCustomAlert(title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertType = type === 'warning' || type === 'info' ? 'error' : type; // Map warning/info to error for styling
    this.showAlert = true;

    // Auto-hide after 10 seconds for non-critical messages
    if (type !== 'error') {
      setTimeout(() => {
        this.showAlert = false;
      }, 10000);
    }
  }

  onAlertClosed(): void {
    this.showAlert = false;
  }

  private onChange(): void {
    this.hasUserInteracted = true;
    this.emitAssignments();
  }

  ngOnInit(): void {
    // Always clear assignments on init and emit empty array
    this.assignments.clear();
    this.emitAssignments();
    this.lastTotalCost = this.totalCost ?? null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalCost']) {
      const prev: number | null = changes['totalCost'].previousValue ?? this.lastTotalCost;
      const curr: number | null = changes['totalCost'].currentValue ?? this.totalCost;

      // If we have existing assignments and a valid previous total, scale proportionally to keep ratios
      if (prev && curr && prev > 0 && curr > 0 && this.assignments && this.assignments.length > 0) {
        const ratio = curr / prev;
        this.assignments.controls.forEach(control => {
          const amtCtrl = control.get('amount');
          const oldVal = Number(amtCtrl?.value) || 0;
          const newVal = Math.round(oldVal * ratio * 100) / 100; // keep 2 decimals
          amtCtrl?.setValue(newVal, { emitEvent: false });
        });
        // Do not mark user interaction here; just synchronize
        this.emitAssignments();
      } else {
        // No scaling needed; still emit to keep parent synced
        this.emitAssignments();
      }

      this.lastTotalCost = curr ?? null;
    }
  }

  ngOnDestroy(): void {
  }

  get assignments() {
    return this.form.get('assignments') as FormArray;
  }

  get totalAssigned(): number {
    return this.assignments.value.reduce((sum: number, a: any) => sum + (Number(a.amount) || 0), 0);
  }

  get remainingAmount(): number {
    return (this.totalCost ?? 0) - this.totalAssigned;
  }

  get progressPercentage(): number {
    if (!this.totalCost) return 0;
    return (this.totalAssigned / this.totalCost) * 100;
  }

  get hasValidTotalCost(): boolean {
    return this.totalCost !== null && this.totalCost !== undefined && this.totalCost > 0;
  }

  // Expose validation to template
  get validation() {
    return this.validateAmounts();
  }

  // Validate budget assignments
  private validateAmounts(): { isValid: boolean; message?: string } {
    // If no product is selected, consider it valid
    if (!this.productId) {
      return { isValid: true };
    }

    if (!this.totalCost || this.totalCost <= 0) {
      return { isValid: false, message: 'Invalid total cost' };
    }

    const totalAssigned = this.assignments.controls.reduce((sum, control) => {
      return sum + (Number(control.get('amount')?.value) || 0);
    }, 0);

    const remaining = this.totalCost - totalAssigned;
    const isExact = Math.abs(remaining) < 0.01; // Allow for floating point precision

    if (!isExact) {
      return {
        isValid: false,
        message: `Total assigned (${totalAssigned.toFixed(2)}) does not match total cost (${this.totalCost.toFixed(2)})`
      };
    }

    return { isValid: true };
  }

  get isValid(): boolean {
    // If no product is selected, form is considered valid until user interacts
    if (!this.productId) {
      return !this.hasUserInteracted;
    }

    // If no assignments yet, form is valid until user interacts
    if (this.assignments.length === 0) {
      return !this.hasUserInteracted;
    }

    // Only validate if user has interacted with the form
    if (!this.hasUserInteracted) {
      return true;
    }

    // At least one valid assignment is required
    const hasValidAssignment = this.assignments.controls.some(control => {
      return control.valid && control.get('amount')?.value > 0;
    });

    return hasValidAssignment && this.validateAmounts().isValid;
  }

  addAssignment(amount: number | null = null): void {
    // Don't add assignment if no product is selected
    if (!this.productId) {
      return;
    }

    // Only mark as interacted if this is a user-initiated action
    if (amount === null) {
      this.hasUserInteracted = true;
    }

    // Only show warning if user explicitly tries to add an assignment with no budgets
    if (this._availableBudgets.length === 0) {
      this.error = 'No budgets available. Please create budgets first.';
      return;
    }

    // Calculate remaining amount
    const remaining = this.remainingAmount;

    // If no amount specified, use the remaining amount or split equally
    // Round to 2 decimal places to avoid floating point precision issues
    const defaultAmount = amount !== null ?
      Math.round(Math.min(amount, remaining) * 100) / 100 :
      Math.round((remaining > 0 ? remaining : this.totalCost || 0) * 100) / 100;

    // Get the first budget that's not already assigned
    const availableBudget = this._availableBudgets.find(budget =>
      !this.assignments.controls.some(control => {
        const controlBudgetId = control.get('budgetID')?.value;
        return controlBudgetId === budget.budgetID;
      })
    ) || this._availableBudgets[0];

    // Use the normalized budget ID
    const budgetId = availableBudget?.budgetID;

    if (!budgetId) {
      console.error('No valid budget ID found');
      return;
    }
    const assignment = this.fb.group({
      budgetID: [budgetId, Validators.required],
      amount: [
        defaultAmount,
        [
          Validators.required,
          Validators.min(0.01),
          (control: AbstractControl): ValidationErrors | null => {
            const value = Number(control.value);
            if (isNaN(value) || value <= 0) {
              return { min: true };
            }
            if (value > this.remainingAmount + value) { // Allow editing up to remaining + current value
              return { max: true };
            }
            return null;
          }
        ]
      ],

      productId: [this.productId],
      productName: [this.productName],
      totalCost: [this.totalCost],
      budgetName: [availableBudget?.budgetName || ''],
      fiscalYear: [availableBudget?.fiscalYear || '']
    });

    // Subscribe to amount changes to update validation
    assignment.get('amount')?.valueChanges.subscribe(() => {
      this.onChange();
    });

    this.assignments.push(assignment);
    this.onChange();
  }

  removeAssignment(index: number) {
    this.assignments.removeAt(index);
    this.emitAssignments();
  }

  onAmountChange() {
    this.hasUserInteracted = true;
    this.emitAssignments();
  }

  // Format amount to 2 decimal places
  formatAmount(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);

    if (!isNaN(value) && value >= 0) {
      const formatted = value.toFixed(2);
      const assignment = this.assignments.at(index);
      if (assignment) {
        assignment.get('amount')?.setValue(parseFloat(formatted), { emitEvent: false });
        input.value = formatted;
      }
    }
  }

  onSaveAssignments(): void {
    // User explicitly clicked save
    this.hasUserInteracted = true;

    // Prepare valid assignments
    const validAssignments = this.assignments.controls
      .map(control => control.value)
      .filter((assignment: any) => assignment.budgetID && Number(assignment.amount) > 0);

    // Only emit when the totals are valid
    const validation = this.validateAmounts();
    if (!validation.isValid || validAssignments.length === 0) {
      return;
    }

    this.assignmentsSave.emit(validAssignments as BudgetAssignment[]);
  }

  private loadProductCost() {
    if (!this.productId) return;

    this.isLoading = true;
    this.error = null;

    const token = localStorage.getItem('authToken');
    if (!token) {
      this.error = 'Authentication required. Please log in again.';
      this.isLoading = false;
      return;
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json'
    });

    this.budgetService.getProductCost(this.productId, headers).subscribe({
      next: (response) => {
        if (response) {
          // Only set productName from the response; totalCost is supplied by the parent
          // as an @Input() (already quantity-scaled) and must not be overwritten here.
          if (response.productName) this.productName = response.productName;
          this.emitAssignments();
        }
        this.isLoading = false;
      },
      error: (err) => {
        // Check if this is a legacy product error
        if ((err as any).isLegacyProductError) {
          this.showCustomAlert(
            'Action Required',
            err.message,
            'warning'
          );
          this.error = 'Product requires cost history setup. See alert message for instructions.';
        } else {
          this.error = 'Failed to load product cost. Please try again.';
        }
        this.isLoading = false;
        console.error('Error loading product cost:', err);
      }
    });
  }

  private emitAssignments(): void {
    // Always emit empty array if no product is selected or no valid assignments
    if (!this.productId || !this.assignments?.length) {
      this.assignmentsChange.emit([]);
      return;
    }

    const validAssignments = this.assignments.controls
      .map(control => control.value)
      .filter((assignment: any) => assignment.budgetID && Number(assignment.amount) > 0);

    this.assignmentsChange.emit(validAssignments);
  }

  private updateFormControls(): void {
    // Update the budget dropdown options when availableBudgets changes
    this.assignments.controls.forEach(control => {
      const budgetControl = control.get('budgetID');
      const amountControl = control.get('amount');

      if (budgetControl) {
        const currentValue = budgetControl.value;
        // If the current value is no longer in availableBudgets, reset it
        if (currentValue && !this._availableBudgets.some(b => b.budgetID === currentValue || b.budgetId === currentValue)) {
          budgetControl.setValue(null);
        }
      }

      // Update validation for amount based on remaining budget
      if (amountControl) {
        amountControl.setValidators([
          Validators.required,
          Validators.min(0.01),
          (control: AbstractControl): ValidationErrors | null => {
            const value = Number(control.value);
            if (isNaN(value) || value <= 0) {
              return { min: true };
            }
            if (value > this.remainingAmount + value) { // Allow editing up to remaining + current value
              return { max: true };
            }
            return null;
          }
        ]);
        amountControl.updateValueAndValidity();
      }
    });

    // Do not auto-add assignments here
  }

  // Handle budget selection change
  onBudgetChange(event: Event, index: number): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    // Parse the selected value to get the budget ID
    // The value is in format "index: budgetId" from the template
    const budgetId = selectedValue.includes(':')
      ? parseInt(selectedValue.split(':')[1].trim(), 10)
      : parseInt(selectedValue, 10);
    // Find the selected budget using the parsed ID
    const budget = this.availableBudgets.find(b =>
      (b.budgetID === budgetId) || (b.budgetId === budgetId)
    );
    if (budget && this.assignments && this.assignments.length > index) {
      const assignment = this.assignments.at(index);
      if (assignment) {
        // Use the correct property name (budgetID) that matches the interface
        const normalizedBudgetId = budget.budgetID || budget.budgetId;
        assignment.patchValue({
          budgetID: normalizedBudgetId,  // Changed from budgetId to budgetID to match the interface
          budgetName: budget.budgetName,
          fiscalYear: budget.fiscalYear
        }, { emitEvent: false }); // Prevent infinite loop
      }
    } else {
      console.error(`Could not find budget with ID ${budgetId} in available budgets`);
    }

    this.onChange();
  }

  // Clear all budget assignments
  clearAssignments(): void {
    if (this.assignments && this.productId) {
      this.assignments.clear();
      this.addEmptyAssignmentRow();
      this.onChange();
    }
  }

  // Add a single empty assignment row (no default budget or amount)
  private addEmptyAssignmentRow(): void {
    const assignment = this.fb.group({
      budgetID: [null, Validators.required],
      amount: [
        null,
        [
          Validators.required,
          Validators.min(0.01),
          (control: AbstractControl): ValidationErrors | null => {
            const value = Number(control.value);
            if (isNaN(value) || value <= 0) {
              return { min: true };
            }
            return null;
          }
        ]
      ],
      productId: [this.productId],
      productName: [this.productName],
      totalCost: [this.totalCost],
      budgetName: [''],
      fiscalYear: ['']
    });

    // Update on amount changes
    assignment.get('amount')?.valueChanges.subscribe(() => {
      this.onChange();
    });

    this.assignments.push(assignment);
    // Do not mark as user interacted for default row
    this.emitAssignments();
  }
}
